// src/view/Project/DataRoom/FilePreview.tsx
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Button,
} from '@mui/material';
import { Close as CloseIcon, Download as DownloadIcon } from '@mui/icons-material';

interface FilePreviewProps {
  open: boolean;
  url: string | null;
  name: string;
  contentType?: string | null;
  onClose: () => void;
}

const FilePreview: React.FC<FilePreviewProps> = ({
  open,
  url,
  name,
  contentType,
  onClose,
}) => {
  if (!open || !url) return null;

  const isImage = contentType?.startsWith('image/');
  const isAudio = contentType?.startsWith('audio/');
  const isPdf = contentType === 'application/pdf';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          bgcolor: '#0d0f12',
          backgroundImage: 'none',
          color: '#ffffff',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '12px',
          boxShadow: '0 16px 40px rgba(0,0,0,0.6)',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
          px: 3,
          py: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontSize: '15px', fontWeight: 600, color: '#ffffff' }} noWrap>
          {name}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            component="a"
            href={url}
            download={name}
            target="_blank"
            rel="noreferrer"
            size="small"
            startIcon={<DownloadIcon />}
            sx={{
              color: '#10B981',
              borderColor: 'rgba(16, 185, 129, 0.3)',
              fontSize: '11px',
              fontWeight: 700,
              textTransform: 'uppercase',
              '&:hover': {
                borderColor: '#10B981',
                bgcolor: 'rgba(16, 185, 129, 0.1)',
              },
            }}
            variant="outlined"
          >
            Download
          </Button>
          <IconButton size="small" onClick={onClose} sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '320px' }}>
        {isImage && (
          <Box
            component="img"
            src={url}
            alt={name}
            sx={{
              maxWidth: '100%',
              maxHeight: '70vh',
              objectFit: 'contain',
              borderRadius: '8px',
            }}
          />
        )}
        {isAudio && (
          <Box sx={{ width: '100%', py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <audio controls src={url} style={{ width: '100%', maxWidth: '500px' }} />
          </Box>
        )}
        {isPdf && (
          <iframe
            src={url}
            title={name}
            style={{ width: '100%', height: '70vh', border: 'none', borderRadius: '8px' }}
          />
        )}
        {!isImage && !isAudio && !isPdf && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2, fontSize: '13px' }}>
              Preview not available for this file type.
            </Typography>
            <Button
              component="a"
              href={url}
              download={name}
              target="_blank"
              rel="noreferrer"
              variant="contained"
              startIcon={<DownloadIcon />}
              sx={{
                bgcolor: '#10B981',
                color: '#000000',
                fontWeight: 700,
                fontSize: '11px',
                textTransform: 'uppercase',
                '&:hover': { bgcolor: '#059669' },
              }}
            >
              Download File
            </Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default FilePreview;
